(function () {
  const TOOL_SELECTOR = 'fa-studio-product-tools';
  const PREVIEW_SELECTOR = '[data-fa-studio-preview]';

  class StudioProductTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = this.readConfig();
      if (!this.config) return;

      this.locale = String(this.config.locale || document.documentElement.lang || 'de-CH');
      this.i18n = { ...(this.config.i18n || {}) };
      this.variants = Array.isArray(this.config.variants) ? this.config.variants : [];
      this.currentVariant = this.findVariant(this.config.variantId) || null;
      this.supportOverrides = {};
      this.deliveryText = '';
      this.deliveryError = false;
      this.deliveryRequestId = 0;
      this.deliveryController = null;
      this.mediaSelectionRequestId = 0;
      this.pendingVariantId = null;

      this.variantUpdateHandler = (event) => this.handleVariantUpdate(event);
      this.optimisticVariantChangeHandler = (event) => this.handleOptimisticVariantChange(event);
      this.cartTriggerHandler = (event) => this.handleCartTrigger(event);
      this.cartUpdateHandler = (event) => this.handleCartUpdate(event);
      this.viewportHandler = () => {
        this.renderLinkedPreviews();
        this.updateStickyBuyBars();
      };

      this.section = this.closest('.shopify-section');
      this.section?.addEventListener('variant:update', this.variantUpdateHandler);
      this.section?.addEventListener('change', this.optimisticVariantChangeHandler, true);
      this.section?.addEventListener('click', this.cartTriggerHandler, true);
      document.addEventListener('cart:update', this.cartUpdateHandler);
      window.addEventListener('scroll', this.viewportHandler, { passive: true });
      window.addEventListener('resize', this.viewportHandler, { passive: true });

      this.renderAll();
      this.bindStickyBuyBars();
      this.loadSupportMap();
      if (this.currentVariant) {
        this.selectVariantMedia(this.currentVariant);
        this.requestDeliveryEstimate();
      }
    }

    disconnectedCallback() {
      this.section?.removeEventListener('variant:update', this.variantUpdateHandler);
      this.section?.removeEventListener('change', this.optimisticVariantChangeHandler, true);
      this.section?.removeEventListener('click', this.cartTriggerHandler, true);
      document.removeEventListener('cart:update', this.cartUpdateHandler);
      window.removeEventListener('scroll', this.viewportHandler);
      window.removeEventListener('resize', this.viewportHandler);
      this.deliveryController?.abort();
    }

    readConfig() {
      const script = this.querySelector('[data-fa-studio-config]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent || '{}');
      } catch {
        return null;
      }
    }

    findVariant(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    handleOptimisticVariantChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('.fa-studio-variant-card input[data-variant-id]') || input.disabled) return;

      const nextVariant = this.findVariant(input.dataset.variantId);
      if (!nextVariant) return;

      const nextVariantId = String(nextVariant.id);
      if (nextVariantId === this.pendingVariantId) return;

      this.currentVariant = nextVariant;
      this.config.variantId = nextVariant.id;
      this.pendingVariantId = nextVariantId;
      this.resetVariantAsyncState();
      this.syncSelectedVariantCard(input);
      this.renderAll();
      this.updateMainPrice(nextVariant);
    }

    handleVariantUpdate(event) {
      const detail = event.detail || {};
      const productId = detail.data?.productId;
      if (productId && String(productId) !== String(this.config.productId)) return;

      const nextVariant = this.findVariant(detail.resource?.id);
      if (!nextVariant) return;

      this.currentVariant = nextVariant;
      this.config.variantId = nextVariant.id;
      this.pendingVariantId = null;
      this.resetVariantAsyncState();

      this.renderAll();
      this.updateMainPrice(nextVariant);
      this.deferVariantMediaSelection(nextVariant);
      this.requestDeliveryEstimate();
    }

    resetVariantAsyncState() {
      this.deliveryRequestId += 1;
      this.deliveryController?.abort();
      this.deliveryController = null;
      this.deliveryText = '';
      this.deliveryError = false;
    }

    syncSelectedVariantCard(selectedInput) {
      this.section?.querySelectorAll('.fa-studio-variant-card input[data-variant-id]').forEach((input) => {
        const isSelected = input === selectedInput;
        input.closest('.fa-studio-variant-card')?.classList.toggle('is-selected', isSelected);
      });
    }

    handleCartTrigger(event) {
      if (this.forwardingStickyCartClick) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('.add-to-cart-button:not(:disabled)');
      const productForm = button?.closest(`product-form-component[data-product-id="${this.config.productId}"]`);
      const stickyCart = button?.closest(`sticky-add-to-cart[data-product-id="${this.config.productId}"]`);
      if (button && (productForm || stickyCart)) this.lastCartTrigger = button;
    }

    handleCartUpdate(event) {
      const data = event.detail?.data || {};
      if (data.source !== 'product-form-component' || data.didError) return;
      if (String(data.productId || '') !== String(this.config.productId)) return;

      const fallbackButton = this.section?.querySelector(
        `.product-details product-form-component[data-product-id="${this.config.productId}"] .add-to-cart-button`
      );
      const source = this.lastCartTrigger?.isConnected ? this.lastCartTrigger : fallbackButton;
      this.lastCartTrigger = null;
      this.playCartSuccessAnimation(source);
    }

    playCartSuccessAnimation(source) {
      const headerDestination = document.querySelector('[data-fleury-cart-icon], .header-actions__cart-icon');
      if (!(source instanceof Element) || !(headerDestination instanceof Element)) return;

      const sourceRect = source.getBoundingClientRect();
      const headerDestinationRect = headerDestination.getBoundingClientRect();
      if (!sourceRect.width || !sourceRect.height || !headerDestinationRect.width || !headerDestinationRect.height) return;

      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const headerDestinationIsVisible = headerDestinationRect.bottom > 0
        && headerDestinationRect.top < viewportHeight
        && headerDestinationRect.right > 0
        && headerDestinationRect.left < viewportWidth;
      let destination = headerDestination;
      let transientDestination = null;

      if (!headerDestinationIsVisible) {
        transientDestination = document.createElement('span');
        transientDestination.className = 'fa-studio-cart-corner-target';
        transientDestination.setAttribute('aria-hidden', 'true');
        const cartIcon = headerDestination.querySelector('svg')?.cloneNode(true);
        if (cartIcon) transientDestination.appendChild(cartIcon);
        document.body.appendChild(transientDestination);
        destination = transientDestination;
      }

      const destinationRect = destination.getBoundingClientRect();

      const startX = sourceRect.left + sourceRect.width / 2;
      const startY = sourceRect.top + sourceRect.height / 2;
      const endX = destinationRect.left + destinationRect.width / 2;
      const endY = destinationRect.top + destinationRect.height / 2;
      const animationId = (this.cartAnimationId || 0) + 1;
      this.cartAnimationId = animationId;
      const token = document.createElement('span');
      token.className = 'fa-studio-cart-flight';
      token.setAttribute('aria-hidden', 'true');
      token.innerHTML = '<span class="fa-studio-cart-flight__icon">+1</span>';
      token.style.setProperty('--fa-cart-start-x', `${startX}px`);
      token.style.setProperty('--fa-cart-start-y', `${startY}px`);
      token.style.setProperty('--fa-cart-mid-x', `${startX + (endX - startX) * 0.56}px`);
      token.style.setProperty('--fa-cart-arc-y', `${Math.max(44, Math.min(startY, endY) - 70)}px`);
      token.style.setProperty('--fa-cart-end-x', `${endX}px`);
      token.style.setProperty('--fa-cart-end-y', `${endY}px`);

      source.classList.remove('fa-studio-cart-source-success');
      destination.classList.remove('fa-studio-cart-target-pulse');
      void source.getBoundingClientRect();
      source.classList.add('fa-studio-cart-source-success');
      destination.classList.add('fa-studio-cart-target-pulse');
      document.body.appendChild(token);

      const cleanup = () => {
        token.remove();
        transientDestination?.remove();
        if (animationId !== this.cartAnimationId) return;
        source.classList.remove('fa-studio-cart-source-success');
        destination.classList.remove('fa-studio-cart-target-pulse');
      };
      token.addEventListener('animationend', cleanup, { once: true });
      window.setTimeout(cleanup, 1400);
    }

    renderAll() {
      const hasSelection = Boolean(this.currentVariant);
      document.body.classList.toggle('fa-studio-no-selection', !hasSelection);
      this.dataset.selectionState = hasSelection ? 'selected' : 'none';
      this.updateSelectionVisibility(hasSelection);

      if (!hasSelection) {
        this.renderUnselectedState();
        return;
      }

      this.renderPanel();
      this.renderLinkedPreviews();
      this.updateDynamicText();
      this.updateStickyBuyBars();
      this.updatePurchaseControls(true, this.pendingVariantId === null);
    }

    renderUnselectedState() {
      this.clearSelectedVariantCard();

      const panel = this.querySelector('[data-fa-studio-panel]');
      if (panel) {
        panel.hidden = true;
        panel.innerHTML = '';
      }

      document.querySelectorAll('[data-fa-studio-page-title]').forEach((element) => {
        element.textContent = this.config.pageTitle;
      });

      document.querySelectorAll('[data-fa-studio-title-pack], [data-fa-studio-pack]').forEach((element) => {
        element.textContent = '';
      });

      document
        .querySelectorAll(`${PREVIEW_SELECTOR}[data-product-id="${this.config.productId}"]`)
        .forEach((preview) => {
          preview.innerHTML = '';
        });

      this.updateStickyBuyBars();
      this.updatePurchaseControls(false, true);
      this.selectGalleryMedia('first');
    }

    updateSelectionVisibility(hasSelection) {
      document.querySelectorAll('[data-fa-studio-title-pack], [data-fa-studio-pack]').forEach((element) => {
        element.hidden = !hasSelection;
      });
      document.querySelectorAll('[data-fa-studio-variant-description]').forEach((element) => {
        element.hidden = !hasSelection;
      });
      document
        .querySelectorAll(`${PREVIEW_SELECTOR}[data-product-id="${this.config.productId}"]`)
        .forEach((preview) => {
          preview.hidden = !hasSelection;
          const previewSection = preview.closest('.fa-studio-section--preview');
          if (previewSection) previewSection.hidden = !hasSelection;
        });
    }

    clearSelectedVariantCard() {
      const picker = this.section?.querySelector(`variant-picker[data-product-id="${this.config.productId}"]`);
      picker?.querySelectorAll('.fa-studio-variant-card input[type="radio"]').forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
        input.dataset.currentChecked = 'false';
        input.dataset.previousChecked = 'false';
        input.closest('.fa-studio-variant-card')?.classList.remove('is-selected');
      });
    }

    updatePurchaseControls(hasSelection, isReady) {
      const available = hasSelection && isReady && this.isInStock();
      const productForm = this.section?.querySelector(
        `.product-details product-form-component[data-product-id="${this.config.productId}"]`
      );
      const button = productForm?.querySelector('.add-to-cart-button');
      const label = button?.querySelector('.add-to-cart-text__content span span');

      if (button) {
        if (!button.dataset.faStudioOriginalLabel && label) {
          button.dataset.faStudioOriginalLabel = label.textContent?.trim() || '';
        }

        if (!hasSelection) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.dataset.faStudioBlocked = 'true';
          if (label) label.textContent = this.t('selectFormat');
        } else {
          button.disabled = !available;
          button.removeAttribute('aria-disabled');
          if (button.dataset.faStudioBlocked === 'true' && label) {
            label.textContent = button.dataset.faStudioOriginalLabel || label.textContent;
          }
          delete button.dataset.faStudioBlocked;
        }
      }

      const nativeStickyButton = this.section
        ?.querySelector(`sticky-add-to-cart[data-product-id="${this.config.productId}"]`)
        ?.querySelector('.sticky-add-to-cart__button');
      if (nativeStickyButton) {
        nativeStickyButton.disabled = !available;
        nativeStickyButton.toggleAttribute('aria-disabled', !available);
      }
    }

    renderPanel() {
      const panel = this.querySelector('[data-fa-studio-panel]');
      if (!panel || !this.currentVariant) return;
      panel.hidden = false;

      const parsed = this.parseVariantTitle(this.currentVariant.title);
      const stock = this.stockState(this.currentVariant);
      panel.innerHTML = `
        <div class="fa-studio-tools">
          <section class="fa-studio-selected" aria-label="${this.escapeHtml(this.config.cardTitle)}">
            <div>
              <span>${this.escapeHtml(this.config.cardTitle)}</span>
              <strong>${this.escapeHtml(parsed.format)}</strong>
              <small>${this.escapeHtml(parsed.pack)}</small>
            </div>
            <ul>
              <li><b>2 cm</b><span>${this.escapeHtml(this.t('depth'))}</span></li>
              <li><b>35 × 20 mm</b><span>${this.escapeHtml(this.t('profile'))}</span></li>
              <li><b>280 g/m²</b><span>${this.escapeHtml(this.t('canvas'))}</span></li>
            </ul>
          </section>
          <div class="fa-studio-logistics">
            <section class="fa-studio-stock fa-studio-stock--${stock.key}" aria-label="${this.escapeHtml(this.t('availability'))}">
              <div class="fa-studio-stock__title">
                <span class="fa-studio-signal" aria-hidden="true"><i></i><i></i></span>
                <strong>${this.escapeHtml(stock.label)}</strong>
              </div>
              <p>${this.escapeHtml(this.t('availabilityHelp'))}</p>
            </section>
            <section class="fa-studio-delivery" aria-live="polite">
              <span>${this.escapeHtml(this.t('estimatedDelivery'))}</span>
              <strong>${this.escapeHtml(this.deliveryStatusText())}</strong>
            </section>
          </div>
        </div>
      `;
    }

    stockState(variant = this.currentVariant) {
      const quantity = this.readNumber(variant?.inventoryQuantity, 0);
      if (!this.isInStock(variant)) return { key: 'out', label: this.t('outOfStock') };
      if (quantity < 5) return { key: 'low', label: this.t('lowStock') };
      if (quantity <= 10) return { key: 'in', label: this.t('inStock') };
      return { key: 'very', label: this.t('veryAvailable') };
    }

    isInStock(variant = this.currentVariant) {
      return Boolean(variant?.available) && this.readNumber(variant?.inventoryQuantity, 0) > 0;
    }

    deliveryStatusText() {
      if (!this.isInStock()) return this.t('byAvailability');
      if (!this.config.deliveryEstimateUrl) return this.t('deliveryUnavailable');
      if (this.deliveryText) return this.deliveryText;
      if (this.deliveryError) return this.t('deliveryReload');
      return this.t('calculating');
    }

    async requestDeliveryEstimate() {
      const variant = this.currentVariant;
      if (!variant || !this.isInStock(variant) || !this.config.deliveryEstimateUrl) return;

      const requestId = ++this.deliveryRequestId;
      const controller = new AbortController();
      this.deliveryController = controller;
      const timeout = window.setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(this.config.deliveryEstimateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            locale: this.locale,
            sku: variant.sku || '',
            thicknessCm: 2,
            productGroup: '',
            inventoryQuantity: this.readNumber(variant.inventoryQuantity, 0),
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) throw new Error(payload.message || 'Delivery estimate failed');
        if (requestId !== this.deliveryRequestId || String(variant.id) !== String(this.currentVariant?.id)) return;

        this.deliveryText = String(payload.estimate?.deliveryText || '').trim() || this.t('byAvailability');
        this.deliveryError = false;
      } catch (error) {
        if (requestId !== this.deliveryRequestId || error?.name === 'AbortError' && controller !== this.deliveryController) return;
        this.deliveryText = '';
        this.deliveryError = true;
      } finally {
        window.clearTimeout(timeout);
        if (requestId === this.deliveryRequestId) {
          this.deliveryController = null;
          this.renderPanel();
        }
      }
    }

    async loadSupportMap() {
      if (!this.config.supportMapUrl) return;
      try {
        const endpoint = new URL(this.config.supportMapUrl, window.location.origin);
        endpoint.searchParams.set('locale', this.locale);
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 10000);
        const response = await fetch(endpoint.toString(), {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        window.clearTimeout(timeout);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok || !payload.overrides) return;
        this.supportOverrides = payload.overrides;
        this.renderLinkedPreviews();
      } catch {
        // The preview keeps using the configurator's standard reinforcement rules.
      }
    }

    parseVariantTitle(title) {
      const source = String(title || '').trim();
      const sizeMatch = source.match(/(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)\s*cm/i);
      const packMatch = source.match(/\(([^)]+)\)\s*$/);
      const shortSide = sizeMatch ? this.readNumber(sizeMatch[1].replace(',', '.'), 0) : 0;
      const longSide = sizeMatch ? this.readNumber(sizeMatch[2].replace(',', '.'), 0) : 0;
      return {
        shortSide,
        longSide,
        format: sizeMatch ? `${sizeMatch[1]} × ${sizeMatch[2]} cm` : source,
        pack: packMatch ? packMatch[1].trim() : '',
      };
    }

    updateDynamicText() {
      if (!this.currentVariant) return;
      const parsed = this.parseVariantTitle(this.currentVariant.title);

      document.querySelectorAll('[data-fa-studio-page-title]').forEach((element) => {
        element.textContent = this.config.pageTitle;
      });
      document.querySelectorAll('[data-fa-studio-title-pack], [data-fa-studio-pack]').forEach((element) => {
        element.textContent = parsed.pack;
        element.hidden = false;
      });
      document.querySelectorAll('[data-fa-studio-format]').forEach((element) => {
        element.textContent = parsed.format;
      });
      document.querySelectorAll('[data-fa-studio-variant-description]').forEach((element) => {
        element.hidden = false;
      });
    }

    updateMainPrice(variant) {
      if (!variant) return;
      const priceContainer = this.section?.querySelector('.product-details [ref="priceContainer"]');
      if (!priceContainer) return;

      const price = this.formatMoney(variant.price);
      const compareAtPrice = this.readNumber(variant.compareAtPrice, 0);
      const hasComparePrice = compareAtPrice > this.readNumber(variant.price, 0);
      const regular = priceContainer.querySelector('.price__regular');
      const sale = priceContainer.querySelector('.price__sale');

      priceContainer.querySelectorAll('.price__regular .price, .price__sale .price-item--sale').forEach((element) => {
        element.textContent = price;
      });
      priceContainer.querySelectorAll('.price__sale .compare-at-price').forEach((element) => {
        element.textContent = hasComparePrice ? this.formatMoney(compareAtPrice) : '';
      });
      regular?.classList.toggle('price__hidden', hasComparePrice);
      sale?.classList.toggle('price__hidden', !hasComparePrice);
    }

    deferVariantMediaSelection(variant) {
      const expectedVariantId = String(variant?.id || '');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (expectedVariantId !== String(this.currentVariant?.id || '')) return;
          this.selectVariantMedia(variant);
        });
      });
    }

    selectVariantMedia(variant) {
      const featuredMediaId = variant?.featuredMediaId || null;
      this.selectGalleryMedia(featuredMediaId ? 'last' : 'first', featuredMediaId);
    }

    selectGalleryMedia(position, expectedMediaId = null) {
      const requestId = ++this.mediaSelectionRequestId;
      const maxAttempts = 24;

      const attemptSelection = (attempt) => {
        if (requestId !== this.mediaSelectionRequestId) return;

        const gallery = document.querySelector(
          `main[data-template="product.studio-keilrahmen"] media-gallery`
        );
        const slideshow = gallery?.slideshow;
        const slides = Array.from(gallery?.querySelectorAll('slideshow-slide.product-media-container') || []);

        if (!slideshow || typeof slideshow.select !== 'function' || !slides.length) {
          if (attempt < maxAttempts) requestAnimationFrame(() => attemptSelection(attempt + 1));
          return;
        }

        if (position === 'last' && expectedMediaId) {
          const zoomItems = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery > li'));
          const selectedZoomItem = zoomItems[zoomItems.length - 1];
          if (!selectedZoomItem || !selectedZoomItem.id.includes(String(expectedMediaId))) {
            if (attempt < maxAttempts) requestAnimationFrame(() => attemptSelection(attempt + 1));
            return;
          }
        }

        const targetIndex = position === 'last' ? slides.length - 1 : 0;
        Promise.resolve(slideshow.select(targetIndex, undefined, { animate: false })).then(() => {
          if (requestId !== this.mediaSelectionRequestId) return;
          if (slideshow.current !== targetIndex && attempt < maxAttempts) {
            requestAnimationFrame(() => attemptSelection(attempt + 1));
          }
        });
      };

      requestAnimationFrame(() => attemptSelection(0));
    }

    renderLinkedPreviews() {
      if (!this.currentVariant) return;
      const markup = this.renderPreview();
      document
        .querySelectorAll(`${PREVIEW_SELECTOR}[data-product-id="${this.config.productId}"]`)
        .forEach((preview) => {
          preview.hidden = false;
          const previewSection = preview.closest('.fa-studio-section--preview');
          if (previewSection) previewSection.hidden = false;
          preview.innerHTML = markup;
        });
    }

    renderPreview() {
      const parsed = this.parseVariantTitle(this.currentVariant?.title);
      if (!parsed.shortSide || !parsed.longSide) return '';

      const length = Math.max(parsed.shortSide, parsed.longSide);
      const width = Math.min(parsed.shortSide, parsed.longSide);
      const maxSide = Math.max(length, width);
      const maxPreviewSize = window.matchMedia('(max-width: 749px)').matches ? 190 : 240;
      const previewWidth = (length / maxSide) * maxPreviewSize;
      const previewHeight = (width / maxSide) * maxPreviewSize;
      const previewRatio = length / width;
      const railSize = 8;
      const labelWidth = Math.min(previewWidth * 0.42, 96);
      const supportType = this.supportTypeFor(length, width, 2);
      const supportLabel = this.supportLabel(supportType);
      const style = `--preview-width:${previewWidth.toFixed(2)}px;--preview-height:${previewHeight.toFixed(2)}px;--preview-ratio:${previewRatio.toFixed(4)};--preview-rail:${railSize}px;--preview-label-width:${labelWidth.toFixed(2)}px;`;
      const labelImage = this.config.labelImage
        ? `<img class="fa-studio-preview-label" src="${this.escapeHtml(this.config.labelImage)}" alt="" width="96" height="65" aria-hidden="true" loading="lazy" decoding="async">`
        : '';

      return `
        <section class="fa-studio-preview" aria-label="${this.escapeHtml(this.t('previewAria'))}">
          <div class="fa-studio-preview__meta">
            <strong>${this.escapeHtml(this.t('preview'))}</strong>
            <span>${this.escapeHtml(parsed.format)} · ${this.escapeHtml(this.config.profileName)} · 2 cm ${this.escapeHtml(this.t('depth'))}</span>
          </div>
          <div class="fa-studio-preview__visuals">
            <div class="fa-studio-preview__stage">
              <div class="fa-studio-preview__measured" style="${style}">
                <span class="fa-studio-preview__measure fa-studio-preview__measure--top"><b>${this.escapeHtml(length)} cm</b></span>
                <span class="fa-studio-preview__measure fa-studio-preview__measure--left"><b>${this.escapeHtml(width)} cm</b></span>
                <div class="fa-studio-preview__frame">
                  <span class="fa-studio-preview__canvas"></span>
                  ${labelImage}
                </div>
              </div>
            </div>
            <div class="fa-studio-preview__stage fa-studio-preview__stage--back">
              <span class="fa-studio-preview__back-label">${this.escapeHtml(supportLabel)}</span>
              ${this.renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType)}
            </div>
          </div>
        </section>
      `;
    }

    renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType) {
      const strutClass = previewWidth >= previewHeight
        ? 'fa-studio-preview__strut fa-studio-preview__strut--vertical'
        : 'fa-studio-preview__strut fa-studio-preview__strut--horizontal';
      const struts = {
        none: '',
        querleiste: `<span class="${strutClass}"></span>`,
        kreuz: '<span class="fa-studio-preview__strut fa-studio-preview__strut--horizontal"></span><span class="fa-studio-preview__strut fa-studio-preview__strut--vertical"></span>',
        doppelkreuz: '<span class="fa-studio-preview__strut fa-studio-preview__strut--horizontal"></span><span class="fa-studio-preview__strut fa-studio-preview__strut--vertical fa-studio-preview__strut--left"></span><span class="fa-studio-preview__strut fa-studio-preview__strut--vertical fa-studio-preview__strut--right"></span>',
      }[supportType] || '';

      return `
        <div class="fa-studio-preview__back-frame" style="--preview-width:${previewWidth.toFixed(2)}px;--preview-height:${previewHeight.toFixed(2)}px;--preview-ratio:${previewRatio.toFixed(4)};--preview-rail:${railSize}px;">
          <span class="fa-studio-preview__back-canvas"></span>
          ${struts}
        </div>
      `;
    }

    supportTypeFor(length, width, thicknessCm) {
      const key = `${thicknessCm}:${Math.min(length, width)}x${Math.max(length, width)}`;
      const override = this.supportOverrides?.[key];
      if (['none', 'querleiste', 'kreuz', 'doppelkreuz'].includes(override)) return override;

      const longSide = Math.max(length, width);
      const shortSide = Math.min(length, width);
      const sidesAtLeast50 = (length >= 50 ? 1 : 0) + (width >= 50 ? 1 : 0);
      if (longSide >= 220) return 'doppelkreuz';
      if (sidesAtLeast50 >= 2) return 'kreuz';
      if (sidesAtLeast50 === 1 || (longSide >= 120 && shortSide >= 20 && shortSide <= 40)) return 'querleiste';
      return 'none';
    }

    supportLabel(type) {
      return {
        none: this.t('supportNone'),
        querleiste: this.t('supportBar'),
        kreuz: this.t('supportCross'),
        doppelkreuz: this.t('supportDoubleCross'),
      }[type] || this.t('supportAuto');
    }

    bindStickyBuyBars() {
      document
        .querySelectorAll(`[data-fa-studio-sticky-bar][data-product-id="${this.config.productId}"]`)
        .forEach((bar) => {
          if (bar.dataset.faStudioBound === 'true') return;
          bar.dataset.faStudioBound = 'true';
          bar.querySelector('[data-fa-studio-sticky-cart]')?.addEventListener('click', (event) => {
            this.lastCartTrigger = event.currentTarget;
            const form = document.querySelector(`product-form-component[data-product-id="${this.config.productId}"]`);
            this.forwardingStickyCartClick = true;
            try {
              form?.querySelector('.add-to-cart-button:not(:disabled)')?.click();
            } finally {
              this.forwardingStickyCartClick = false;
            }
          });
        });
    }

    updateStickyBuyBars() {
      if (!this.currentVariant) {
        document
          .querySelectorAll(`[data-fa-studio-sticky-bar][data-product-id="${this.config.productId}"]`)
          .forEach((bar) => {
            bar.classList.remove('is-visible');
            const button = bar.querySelector('[data-fa-studio-sticky-cart]');
            if (button) button.disabled = true;
          });
        return;
      }
      const parsed = this.parseVariantTitle(this.currentVariant.title);
      const shouldShow = true;
      const available = this.pendingVariantId === null && this.isInStock();

      document
        .querySelectorAll(`[data-fa-studio-sticky-bar][data-product-id="${this.config.productId}"]`)
        .forEach((bar) => {
          bar.classList.toggle('is-visible', shouldShow);
          const title = bar.querySelector('[data-fa-studio-sticky-title]');
          const pack = bar.querySelector('[data-fa-studio-sticky-pack]');
          const price = bar.querySelector('[data-fa-studio-sticky-price]');
          const button = bar.querySelector('[data-fa-studio-sticky-cart]');
          if (title) title.textContent = `${this.config.pageTitle} ${parsed.format}`;
          if (pack) pack.textContent = parsed.pack;
          if (price) price.textContent = this.formatMoney(this.currentVariant.price);
          if (button) button.disabled = !available;
          bar.toggleAttribute('aria-busy', this.pendingVariantId !== null);
        });
    }

    formatMoney(cents) {
      const currency = String(this.config.currency || 'CHF').toUpperCase();
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency }).format(this.readNumber(cents, 0) / 100);
      } catch {
        return `${currency} ${(this.readNumber(cents, 0) / 100).toFixed(2)}`;
      }
    }

    readNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }

    t(key) {
      return this.i18n[key] || key;
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get(TOOL_SELECTOR)) {
    customElements.define(TOOL_SELECTOR, StudioProductTools);
  }
})();
